import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ProductCategory } from '../../types';
import { productService, UpdateCategoryRequest } from '../../services/productService';
import { chartOfAccountsService, GLAccountDTO } from '../../services/accountingService';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

import { getServerErrorMessage } from '../../utils/errorUtils';

const ACCOUNT_MAPPING_FIELDS = ['defaultInventoryAccountId', 'defaultCOGSAccountId', 'defaultRevenueAccountId'];

const ProductCategoryEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]);
  const [glAccounts, setGlAccounts] = useState<GLAccountDTO[]>([]);
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UpdateCategoryRequest>({
    name: '',
    description: '',
    parentCategoryId: undefined,
    isActive: true,
    defaultInventoryAccountId: null,
    defaultCOGSAccountId: null,
    defaultRevenueAccountId: null,
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoryRes, allCategoriesRes] = await Promise.all([
        productService.getCategoryById(parseInt(id || '0')),
        productService.getCategories({ pageSize: 100 }),
      ]);

      if (categoryRes.data.data) {
        const cat = categoryRes.data.data as ProductCategory;
        setCategory(cat);
        setFormData({
          name: cat.name,
          description: cat.description || '',
          parentCategoryId: cat.parentCategoryId,
          isActive: cat.isActive,
          defaultInventoryAccountId: cat.defaultInventoryAccountId ?? null,
          defaultCOGSAccountId: cat.defaultCOGSAccountId ?? null,
          defaultRevenueAccountId: cat.defaultRevenueAccountId ?? null,
        });
      }

      if (allCategoriesRes.data.data) {
        setAllCategories(allCategoriesRes.data.data as ProductCategory[]);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load category data');
      setLoading(false);
    }

    // Optional — the Accounting app may not be enabled for this company, so a
    // failure here shouldn't block the category form from loading.
    try {
      const glRes = await chartOfAccountsService.getAll(false);
      const data = (glRes?.data as any)?.data ?? glRes?.data ?? [];
      if (Array.isArray(data)) setGlAccounts(data);
    } catch {
      setGlAccounts([]);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'parentCategoryId'
          ? (value ? parseInt(value) : undefined)
          : ACCOUNT_MAPPING_FIELDS.includes(name)
          ? (value ? parseInt(value) : null)
          : value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Category name is required');
      return false;
    }
    if (formData.name.length > 30) {
      setError('Category name cannot exceed 30 characters');
      return false;
    }
    if (formData.description && formData.description.length > 200) {
      setError('Description cannot exceed 200 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !category) {
      return;
    }

    setSaving(true);
    try {
      await productService.updateCategory(category.id, formData);
      navigate('/products/categories', {
        state: { message: 'Category updated successfully!' },
      });
    } catch (err: any) {
      setError(
        getServerErrorMessage(err) || 'Failed to update category'
      );
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="edit-spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">Category not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/categories')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              Edit Category
              <span className="ims-page-subtitle">{category.name}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/categories')} disabled={saving}>
              Cancel
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />Saving...</> : <><Save size={15} strokeWidth={2} />Save Changes</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="edit-content">
          {/* Form Card */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">Category Details</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">
                      Category Name <span className="required">*</span>
                      <span className="char-counter">{formData.name.length}/30</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={30}
                      placeholder="Enter category name"
                      required
                    />
                    <small className="form-helper">e.g., Electronics, Furniture, Clothing</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Description
                      <span className="char-counter">{(formData.description || '').length}/200</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      maxLength={200}
                      placeholder="Enter category description"
                      rows={4}
                    />
                    <small className="form-helper">Provide a brief description of this category</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Parent Category (Optional)</label>
                    <select
                      className="form-select"
                      name="parentCategoryId"
                      value={formData.parentCategoryId || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">-- No Parent (Root Category) --</option>
                      {allCategories
                        .filter((c) => c.id !== category.id)
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                    <small className="form-helper">
                      Select a parent category if this is a sub-category. Cannot select itself as parent.
                    </small>
                  </div>

                  {/* ── Active status ── */}
                  <div className="form-group">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary)' }}
                      />
                      <label
                        htmlFor="isActive"
                        className="form-label"
                        style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}
                      >
                        Active
                      </label>
                    </div>
                    <small className="form-helper">
                      Inactive categories will not be available for product assignment.
                    </small>
                  </div>

                  {glAccounts.length > 0 && (
                    <>
                      <div className="form-group" style={{ marginTop: 8, marginBottom: 4 }}>
                        <label className="form-label" style={{ fontWeight: 700 }}>Accounting (Optional)</label>
                        <small className="form-helper">
                          Override which GL accounts this category's sales, cost of goods, and inventory movements post to.
                          Leave blank to use the company's default accounts.
                        </small>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Revenue Account</label>
                        <select
                          className="form-select"
                          name="defaultRevenueAccountId"
                          value={formData.defaultRevenueAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Revenue').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Cost of Goods Sold Account</label>
                        <select
                          className="form-select"
                          name="defaultCOGSAccountId"
                          value={formData.defaultCOGSAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Expense').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Inventory Account</label>
                        <select
                          className="form-select"
                          name="defaultInventoryAccountId"
                          value={formData.defaultInventoryAccountId ?? ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Use company default --</option>
                          {glAccounts.filter((a) => a.accountType === 'Asset').map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-info-section">
            {/* Category Info Card */}
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">Category Information</h3>
              </div>
              <div className="edit-card-body">
                <div className="info-item">
                  <label className="info-label">Category ID</label>
                  <div className="info-value">{category.id}</div>
                </div>

                <div className="info-item">
                  <label className="info-label">Type</label>
                  <div className="info-value">
                    {category.parentCategoryId ? (
                      <span className="info-badge info-badge-sub">Sub-category</span>
                    ) : (
                      <span className="info-badge info-badge-root">Root Category</span>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">Status</label>
                  <div className="info-value">
                    {formData.isActive ? (
                      <span className="info-badge info-badge-active">ACTIVE</span>
                    ) : (
                      <span className="info-badge info-badge-inactive">INACTIVE</span>
                    )}
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">Created Date</label>
                  <div className="info-value">
                    {new Date(category.createdAt).toLocaleDateString('en-US')}
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">Created Time</label>
                  <div className="info-value">
                    {new Date(category.createdAt).toLocaleTimeString('en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Help Card */}
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">Need Help?</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>Root Category</strong>
                  <p>A category without a parent, typically used for main product categories.</p>
                </div>
                <div className="help-item">
                  <strong>Sub-category</strong>
                  <p>A category under another category, useful for organizing products hierarchically.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductCategoryEditPage;
